// 測定セッションのリーグ別永続化（§14）。persist.ts（farmsets）と同型パターン。
import type { FarmSet, GroupMember } from './schema';
import { isGroupDrop } from './schema';
import { newId } from './persist';
import { MEASURE_SCHEMA_VERSION } from './measure-schema';
import type { MeasureDropCounter, MeasureExtraCost, MeasureSession } from './measure-schema';

const PREFIX = 'poe2farm';

function sessionsKey(league: string): string {
  return `${PREFIX}:measure-sessions:${league}`;
}

function activeKey(league: string): string {
  return `${PREFIX}:measure-active:${league}`;
}

/** 指定リーグの全セッション。 */
export function loadSessions(league: string): MeasureSession[] {
  const raw = localStorage.getItem(sessionsKey(league));
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? (v as MeasureSession[]) : [];
  } catch {
    return [];
  }
}

function writeSessions(league: string, sessions: MeasureSession[]): void {
  localStorage.setItem(sessionsKey(league), JSON.stringify(sessions));
}

/** セッションを所属リーグのバケットに upsert（updatedAt を更新）。 */
export function saveSession(session: MeasureSession): MeasureSession {
  const stored: MeasureSession = { ...session, updatedAt: Date.now() };
  const sessions = loadSessions(session.league);
  const idx = sessions.findIndex((s) => s.id === session.id);
  if (idx >= 0) sessions[idx] = stored;
  else sessions.push(stored);
  writeSessions(session.league, sessions);
  return stored;
}

export function deleteSession(league: string, id: string): void {
  writeSessions(
    league,
    loadSessions(league).filter((s) => s.id !== id),
  );
}

export function getActiveSessionId(league: string): string | null {
  return localStorage.getItem(activeKey(league));
}

export function setActiveSessionId(league: string, id: string): void {
  localStorage.setItem(activeKey(league), id);
}

/**
 * ドロップカウンタ生成（adhoc）。
 * runs = 追加時点の session.runs（観測窓の開始。以前の周回に k を対応づけない）。
 */
export function createDropCounter(unit: string, runs = 0): MeasureDropCounter {
  return { id: newId(), name: '', unit, value: 0, count: 0, importedCount: 0, importedRuns: runs, source: { kind: 'adhoc' } };
}

/** 臨時コスト行生成。 */
export function createExtraCost(unit: string): MeasureExtraCost {
  return { id: newId(), name: '', unit, value: 0, count: 1 };
}

/**
 * リンクセットの dropLines / GroupDrop.members からカウンタ群を生成する。
 * グループメンバーは source=set-member（取り込みはグループ単位で全メンバー一括）。
 * runs = 生成時点の session.runs（新規セッションは 0、途中同期はその時点の値）。
 */
export function countersFromSet(set: FarmSet, runs = 0): MeasureDropCounter[] {
  const counters: MeasureDropCounter[] = [];
  for (const entry of set.dropLines) {
    if (isGroupDrop(entry)) {
      for (const mem of entry.members) counters.push(memberCounter(entry.id, mem, runs));
    } else {
      counters.push({
        id: newId(),
        name: entry.name,
        itemId: entry.itemId,
        unit: entry.unit,
        value: entry.value,
        count: 0,
        importedCount: 0,
        importedRuns: runs,
        source: { kind: 'set-line', lineId: entry.id },
      });
    }
  }
  return counters;
}

function memberCounter(groupId: string, mem: GroupMember, runs: number): MeasureDropCounter {
  return {
    id: newId(),
    name: mem.name,
    itemId: mem.itemId,
    unit: mem.unit,
    value: mem.value,
    count: 0,
    importedCount: 0,
    importedRuns: runs,
    source: { kind: 'set-member', groupId, memberId: mem.id },
  };
}

/** 新規セッション。リンクセットを渡すとカウンタを自動生成する。 */
export function createMeasureSession(league: string, linkedSet?: FarmSet): MeasureSession {
  const now = Date.now();
  return {
    schemaVersion: MEASURE_SCHEMA_VERSION,
    id: newId(),
    name: linkedSet ? `${linkedSet.name} の測定` : '新しい測定',
    league,
    linkedSetId: linkedSet?.id,
    elapsedMs: 0,
    runningSince: null,
    lastTickAt: null,
    runs: 0,
    drops: linkedSet ? countersFromSet(linkedSet) : [],
    extraCosts: [],
    createdAt: now,
    updatedAt: now,
  };
}
